import { useCallback, useMemo, useState, useEffect } from 'react';
import { I18n } from 'react-redux-i18n';
import { Flex } from 'antd';
import styled from 'styled-components';

import GlobalDataTable from './Table';
import GlobalViewContainer from './container';
import CategoryToggle from './CategoryToggle';
import WorldMap from './components/WorldMap';
import { CategoryProvider } from './context/CategoryContext';

import {
  useDistributionGlobalDataFilters,
  useDistributionGlobalData,
  useDistributionGlobalDataCount
} from 'apiTyped/hooks/global_data_view.query';
import GlobalViewFilters from './Filters';
import { useUserMe } from 'apiTyped/hooks/users.query';
import {
  isSpiritOnly,
  showNCBars,
  showSpiritGeographyFilters
} from 'utils/personalisation';

const PRICE_RANGE_MIN = 0;
const PRICE_RANGE_MAX = 10000;
// Matches wine_format_group.id for 'bottle' (see api get_wine_format_groups) -
// wine analysis should default to standard 750ml bottles rather than "All sizes".
const WINE_BOTTLE_FORMAT_ID = 2;

const UnderlinedSpan = styled.span`
  text-decoration: underline;
  display: inline-block;
  perspective: 1000px;
`;

const CylinderContainer = styled.span`
  display: inline-block;
  position: relative;
  font-style: italic;
  transform-style: preserve-3d;
  transition: transform 0.6s ease-in-out;

  &.rotate {
    transform: rotateX(180deg);
  }
`;

const CylinderSide = styled.span`
  display: inline-block;
  backface-visibility: hidden;

  &.front {
    transform: rotateX(0deg);
  }

  &.back {
    position: absolute;
    left: 0;
    transform: rotateX(180deg);
  }
`;

const GlobalView = () => {
  const { user } = useUserMe();
  const clientTags = useMemo(() => user.client.tags || [], [user]);

  const isSpiritOnlyFlag = isSpiritOnly(clientTags);
  const hasSpirit = showNCBars(clientTags);

  // Show toggle only if user has Spirit AND other tags
  const shouldShowCategoryToggle = hasSpirit && !isSpiritOnlyFlag;

  const defaultState = useMemo(
    () => ({
      network: 'on',
      zone: 'world',
      country: '231',
      markets: [],
      surveDate: 'flow',
      wineAppelation: [],
      wineColor: 'all',
      wineCountry: 'all',
      wineRegion: 'all',
      // Default to Spirit ('2') if spirit-only, otherwise Wine ('1')
      wineType: isSpiritOnlyFlag ? '2' : '1',
      wineSubType: 'all',
      wineGrapeVariety: [],
      wineFormatGroup: isSpiritOnlyFlag ? 'all' : WINE_BOTTLE_FORMAT_ID,
      rangePrices: [PRICE_RANGE_MIN, PRICE_RANGE_MAX],
      tableTypeActive: 'point_of_sale',
      search: '',
      storePriceBand: [],
      storeFacingBand: [],
      storeAwards: [],
      storeWsAward: [],
      storeMichelinStar: [],
      vintage: [],
      storePrimaryType: [],
      storeRating: [],
      clientRefs: [],
      clientCustomRefs: [],
      posStatus: 'all',
      spiritFormatGroup: 'all'
    }),
    [isSpiritOnlyFlag]
  );

  // Load initial state from localStorage
  const getInitialState = () => {
    try {
      const savedFilters = localStorage.getItem('global_view_filters');
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        // Migrate legacy wineAppelation from string to array
        if (!Array.isArray(parsed.wineAppelation)) {
          parsed.wineAppelation = [];
        }
        return { ...defaultState, ...parsed, country: defaultState.country };
      }
    } catch (e) {
      console.error('Error loading global view filters from localStorage:', e);
    }
    return defaultState;
  };

  const [state, setState] = useState(getInitialState());

  // Save filters to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem('global_view_filters', JSON.stringify(state));
    } catch (e) {
      console.error('Error saving global view filters to localStorage:', e);
    }
  }, [state]);

  const { isLoadingFilters: isLoadingFilterOptions } =
    useDistributionGlobalDataFilters({
      network: state.network,
      survey_year: state.surveDate === 'flow' ? 'current' : state.surveDate
    });

  // done this way for notebooks:
  // const [filterValue, setFilterValue] = useState<NotebookFilter>({
  //   network: undefined,
  //   country: undefined,
  //   market: undefined,
  //   year: undefined,
  //   subscribed: false
  // });

  const aggregation_type =
    state.tableTypeActive === 'spirit' ? 'wine' : state.tableTypeActive;

  const { data = [], isLoadingFilters: isLoadingData } =
    useDistributionGlobalData({
      currency: 'USD',
      include_status_metrics: false,
      ...(['wine', 'brand'].includes(aggregation_type)
        ? { result_limit: 1000 }
        : {}),
      network: state.network,
      continent_id: state.zone === 'world' ? null : state.zone,
      country_id: state.country === 'all' ? null : state.country,
      market_id:
        state.markets.length > 0
          ? state.markets.map((item) => item.value)
          : null,
      survey_year: state.surveDate === 'flow' ? 'current' : state.surveDate,
      wine_color_id: state.wineColor === 'all' ? null : state.wineColor,
      wine_type_id: state.wineType,
      wine_sub_type_id: state.wineSubType === 'all' ? null : state.wineSubType,
      wine_grape_variety_id:
        state.wineGrapeVariety.length > 0
          ? state.wineGrapeVariety.map((item) => item.value)
          : null,
      wine_format_group_id:
        state.wineFormatGroup === 'all' ? null : state.wineFormatGroup,
      wine_region_id: state.wineRegion === 'all' ? null : state.wineRegion,
      wine_country_id: state.wineCountry === 'all' ? null : state.wineCountry,
      wine_appelation_id:
        Array.isArray(state.wineAppelation) && state.wineAppelation.length > 0
          ? state.wineAppelation.map((item) => item.value)
          : null,
      spirit_format_group_id:
        state.spiritFormatGroup === 'all' ? null : state.spiritFormatGroup,
      store_menu_price_band_id:
        state.storePriceBand.length > 0
          ? state.storePriceBand.map((item) => item.value)
          : null,
      store_facing_band_id:
        state.storeFacingBand.length > 0
          ? state.storeFacingBand.map((item) => item.value)
          : null,
      store_awards_id:
        state.storeAwards.length > 0
          ? state.storeAwards.map((item) => item.value)
          : null,
      store_ws_award:
        state.storeWsAward.length > 0
          ? state.storeWsAward.map((item) => item.value)
          : null,
      store_star:
        state.storeMichelinStar.length > 0
          ? state.storeMichelinStar.map((item) => item.value)
          : null,
      store_primary_sub_type_id:
        state.storePrimaryType.length > 0
          ? state.storePrimaryType.map((item) => item.value)
          : null,
      store_review_rating_id:
        state.storeRating.length > 0
          ? state.storeRating.map((item) => item.value)
          : null,
      vintage:
        state.vintage.length > 0
          ? state.vintage.map((item) => item.value)
          : null,
      selected_client_ref_ids:
        state.clientRefs.length > 0
          ? state.clientRefs.map((item) => item.value)
          : null,
      selected_client_custom_ids:
        state.clientCustomRefs.length > 0
          ? state.clientCustomRefs.map((item) => item.value)
          : null,
      // tableTypeActive = spirit, wine, brand, appellation, guide_michelin, wine_spectator
      // for wine and spirit we send wine, for brand, we send brand else store
      aggregation_type: aggregation_type,
      pos_status: state.posStatus === 'all' ? null : state.posStatus,
      rangePrices: state.rangePrices,
      search: state.search
    });

  const isLoading = isLoadingFilterOptions || isLoadingData;

  const { posCount = undefined } = useDistributionGlobalDataCount({
    currency: 'USD',
    network: state.network,
    continent_id: state.zone === 'world' ? null : state.zone,
    country_id: state.country === 'all' ? null : state.country,
    market_id:
      state.markets.length > 0 ? state.markets.map((item) => item.value) : null,
    survey_year: state.surveDate === 'flow' ? 'current' : state.surveDate,
    wine_color_id: state.wineColor === 'all' ? null : state.wineColor,
    wine_type_id: state.wineType,
    wine_sub_type_id: state.wineSubType === 'all' ? null : state.wineSubType,
    wine_grape_variety_id:
      state.wineGrapeVariety.length > 0
        ? state.wineGrapeVariety.map((item) => item.value)
        : null,
    wine_format_group_id:
      state.wineFormatGroup === 'all' ? null : state.wineFormatGroup,
    wine_region_id: state.wineRegion === 'all' ? null : state.wineRegion,
    wine_country_id: state.wineCountry === 'all' ? null : state.wineCountry,
    wine_appelation_id:
      Array.isArray(state.wineAppelation) && state.wineAppelation.length > 0
        ? state.wineAppelation.map((item) => item.value)
        : null,
    spirit_format_group_id:
      state.spiritFormatGroup === 'all' ? null : state.spiritFormatGroup,
    store_menu_price_band_id:
      state.storePriceBand.length > 0
        ? state.storePriceBand.map((item) => item.value)
        : null,
    store_facing_band_id:
      state.storeFacingBand.length > 0
        ? state.storeFacingBand.map((item) => item.value)
        : null,
    store_awards_id:
      state.storeAwards.length > 0
        ? state.storeAwards.map((item) => item.value)
        : null,
    store_ws_award:
      state.storeWsAward.length > 0
        ? state.storeWsAward.map((item) => item.value)
        : null,
    store_star:
      state.storeMichelinStar.length > 0
        ? state.storeMichelinStar.map((item) => item.value)
        : null,
    store_primary_sub_type_id:
      state.storePrimaryType.length > 0
        ? state.storePrimaryType.map((item) => item.value)
        : null,
    store_review_rating_id:
      state.storeRating.length > 0
        ? state.storeRating.map((item) => item.value)
        : null,
    vintage:
      state.vintage.length > 0 ? state.vintage.map((item) => item.value) : null,
    selected_client_ref_ids:
      state.clientRefs.length > 0
        ? state.clientRefs.map((item) => item.value)
        : null,
    selected_client_custom_ids:
      state.clientCustomRefs.length > 0
        ? state.clientCustomRefs.map((item) => item.value)
        : null,
    // tableTypeActive = spirit, wine, brand, appellation, guide_michelin, wine_spectator
    // for wine and spirit we send wine, for brand, we send brand else store
    aggregation_type: 'point_of_sale',
    pos_status: state.posStatus === 'all' ? null : state.posStatus,
    rangePrices: state.rangePrices
  });

  // params for the export button:
  const exportUrl = `/global_data_view/xls?`;
  const exportParams = {
    currency: 'USD',
    network: state.network,
    export_type: ['brand', 'wine'].includes(aggregation_type)
      ? `wines`
      : aggregation_type,
    continent_id: state.zone === 'world' ? null : state.zone,
    country_id: state.country === 'all' ? null : state.country,
    market_id:
      state.markets.length > 0 ? state.markets.map((item) => item.value) : null,
    survey_year: state.surveDate === 'flow' ? 'current' : state.surveDate,
    wine_color_id: state.wineColor === 'all' ? null : state.wineColor,
    wine_type_id: state.wineType,
    wine_sub_type_id: state.wineSubType === 'all' ? null : state.wineSubType,
    wine_grape_variety_id:
      state.wineGrapeVariety.length > 0
        ? state.wineGrapeVariety.map((item) => item.value)
        : null,
    wine_format_group_id:
      state.wineFormatGroup === 'all' ? null : state.wineFormatGroup,
    wine_region_id: state.wineRegion === 'all' ? null : state.wineRegion,
    wine_country_id: state.wineCountry === 'all' ? null : state.wineCountry,
    wine_appelation_id:
      Array.isArray(state.wineAppelation) && state.wineAppelation.length > 0
        ? state.wineAppelation.map((item) => item.value)
        : null,
    store_menu_price_band_id:
      state.storePriceBand.length > 0
        ? state.storePriceBand.map((item) => item.value)
        : null,
    store_facing_band_id:
      state.storeFacingBand.length > 0
        ? state.storeFacingBand.map((item) => item.value)
        : null,
    store_awards_id:
      state.storeAwards.length > 0
        ? state.storeAwards.map((item) => item.value)
        : null,
    store_ws_award:
      state.storeWsAward.length > 0
        ? state.storeWsAward.map((item) => item.value)
        : null,
    store_star:
      state.storeMichelinStar.length > 0
        ? state.storeMichelinStar.map((item) => item.value)
        : null,
    store_primary_sub_type_id:
      state.storePrimaryType.length > 0
        ? state.storePrimaryType.map((item) => item.value)
        : null,
    store_review_rating_id:
      state.storeRating.length > 0
        ? state.storeRating.map((item) => item.value)
        : null,
    vintage:
      state.vintage.length > 0 ? state.vintage.map((item) => item.value) : null,
    selected_client_ref_ids:
      state.clientRefs.length > 0
        ? state.clientRefs.map((item) => item.value)
        : null,
    selected_client_custom_ids:
      state.clientCustomRefs.length > 0
        ? state.clientCustomRefs.map((item) => item.value)
        : null,
    aggregation_type: aggregation_type,
    pos_status: state.posStatus === 'all' ? null : state.posStatus,
    search: state.search,
    min_price: state.rangePrices?.[0] ?? null,
    max_price: state.rangePrices?.[1] ?? null,
    spirit_format_group_id:
      state.spiritFormatGroup === 'all' ? null : state.spiritFormatGroup
  };

  const currency = 'USD';

  const onClearFilters = useCallback(() => {
    setState((prevState) => ({
      ...defaultState,
      tableTypeActive: prevState.tableTypeActive,
      wineType: prevState.wineType,
      wineFormatGroup:
        prevState.wineType === '1' ? WINE_BOTTLE_FORMAT_ID : 'all'
    }));
  }, [defaultState]);

  const setSearch = useCallback((search) => {
    setState((prevState) => ({ ...prevState, search }));
  }, []);

  const setTableTypeActive = useCallback((tableTypeActive) => {
    setState((prevState) => {
      const isPOS = tableTypeActive === 'point_of_sale';
      const isWine = tableTypeActive === 'wine';
      const isPositioning = tableTypeActive === 'positioning';

      return {
        ...prevState,
        tableTypeActive,
        search: '',
        // Reset filters not visible on the target tab
        clientCustomRefs: isPOS || isWine ? prevState.clientCustomRefs : [],
        posStatus: isPOS ? prevState.posStatus : 'all',
        vintage: isWine ? prevState.vintage : [],
        rangePrices: isPositioning
          ? [PRICE_RANGE_MIN, PRICE_RANGE_MAX]
          : prevState.rangePrices
      };
    });
  }, []);

  const setWineType = useCallback((wineType) => {
    setState((prevState) => ({
      ...prevState,
      wineType,
      wineColor: 'all',
      wineCountry: 'all',
      wineSubType: 'all',
      wineRegion: 'all',
      wineAppelation: [],
      wineGrapeVariety: [],
      wineFormatGroup: wineType === '1' ? WINE_BOTTLE_FORMAT_ID : 'all'
    }));
  }, []);

  const isSpirit = state.wineType === '2';
  return (
    <CategoryProvider value={{ wineType: state.wineType }}>
      <GlobalViewContainer>
        <WorldMap
          className="world-map-background"
          seaColor={isSpirit ? '#272d35' : '#191e25'}
          landColor={isSpirit ? '#191e25' : '#272d35'}
          borderColor={isSpirit ? '#272d35' : '#191e25'}
          borderWidth={0.5}
          zoom={1.4}
        />

        <div className="bodyContainer">
          <Flex justify="space-around" gap={30} vertical>
            <div className="titleContainer">
              <Flex align="center" gap={20} justify="space-between">
                <div>
                  <h3 className="title">{I18n.t('globalView.title')}</h3>
                  <h1 className="subtitle">
                    <span> {I18n.t(`globalView.subTitle.main`)}</span>
                    <UnderlinedSpan>
                      <CylinderContainer
                        className={state.wineType === '2' ? 'rotate' : ''}
                      >
                        <CylinderSide className="front">
                          {I18n.t('globalView.subTitle.wines')}
                        </CylinderSide>
                        <CylinderSide className="back">
                          {I18n.t('globalView.subTitle.spirits')}
                        </CylinderSide>
                      </CylinderContainer>
                    </UnderlinedSpan>
                  </h1>
                </div>
                {shouldShowCategoryToggle && (
                  <CategoryToggle
                    value={state.wineType}
                    onChange={setWineType}
                  />
                )}
              </Flex>
            </div>
            <GlobalViewFilters
              stateFilters={state}
              onChangeFilters={setState}
              onClearFilters={onClearFilters}
              dnForNetwork={posCount}
              currency={currency}
              spiritGeographyFiltersEnabled={showSpiritGeographyFilters(
                user.client.id
              )}
              // Keep fixed range for price slider for now
              // limit={[minPrice, maxPrice]}
              limit={[PRICE_RANGE_MIN, PRICE_RANGE_MAX]}
            />
            <GlobalDataTable
              activeTab={state.tableTypeActive}
              data={data}
              filters={state}
              currency={currency}
              isLoading={isLoading}
              setTableTypeActive={setTableTypeActive}
              exportUrl={exportUrl}
              exportParams={exportParams}
              posCount={posCount}
              onChangeFilters={setState}
              onClearFilters={onClearFilters}
              setSearch={setSearch}
            />
          </Flex>
        </div>
      </GlobalViewContainer>
    </CategoryProvider>
  );
};

export default GlobalView;
